import { useDisclosure, useMediaQuery } from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import navWithPermissions from '../../_nav'
import { useMFAdminAppInitializer } from '../../contexts/MFAdminAppInitializer'
import { useKeyPress } from '../../hooks/useKeyPress'

//(property) setModalOpen: React.Dispatch<React.SetStateAction<boolean>>

/**
 * @typedef {ReturnType<typeof useDisclosure> & {
 *   skin: {
 *    value: import('../../contexts/MFAdminAppInitializer').MFAdminLayoutSkinType;
 *    toggle: import('react').Dispatch<import('react').SetStateAction<boolean>>;
 *   };
 *   menu: {
 *    size: {
 *      value: import('../../contexts/MFAdminAppInitializer').MFAdminLayoutMenu['state'];
 *      toggle: import('../../contexts/MFAdminAppInitializer').MFAdminLayoutActions['SET_MENU_STATE'];
 *    };
 *    state: {
 *      trueDockState: boolean;
 *      isHovered: boolean;
 *      setIsHovered: import('react').Dispatch<import('react').SetStateAction<boolean>>;
 *    };
 *   };
 *   current: {
 *    admin: import('../../reducers/adminsReducer').CurrentAdmin;
 *    agency: import('../../reducers/currentReducer').CurrentAgency;
 *    org: import('../../reducers/currentReducer').CurrentOrganization;
 *    loggedInAs: 'Agency' | 'Organization';
 *    type: 'Super Admin' | 'Client Admin' | 'Admin';
 *   },
 *   modal: {
 *    value: boolean,
 *    toggle: import('react').Dispatch<import('react').SetStateAction<boolean>>
 *  };
 * }} MFNavContextType
 */

/**
 * @type {import('react').Context<MFNavContextType | null>}
 */
const MFNavContext = createContext(null)

/**
 * Custom hook to access the Sidenav context.
 *
 * @throws {Error} If the hook is used outside of the MFNavProvider.
 * @returns {MFNavContextType} The nav context value with additional properties.
 */
export function useMFNav() {
  /** @type {MFNavContextType | null} */
  const mfNav = useContext(MFNavContext)

  if (!mfNav) {
    throw new Error('Cannot use `mfNav context` outside MFNavProvider')
  }

  return { ...mfNav }
}

/**
 * MFNavProvider component to provide Sidenav context to its children.
 *
 * @param {object} props - The props for the provider.
 * @param {React.ReactNode} props.children - The children components that will have access to the context.
 * @returns {JSX.Element} The provider component that supplies the Sidenav context.
 */
export default function MFNavProvider({ children, ...props }) {
  /** @type {import('../../reducers/adminsReducer').CurrentAdmin} */
  const currentAdmin = useSelector(state => state.admins.current_admin)
  /** @type {import('../../reducers/currentReducer').CurrentAgency} */
  const currentAgency = useSelector(state => state.current.agency)
  /** @type {import('../../reducers/currentReducer').CurrentOrganization} */
  const currentOrg = useSelector(state => state.current.organization)

  const {
    state: {
      layout: { skin, menu }
    },
    actions: {
      layout: { SET_MENU_STATE, SET_SKIN }
    }
  } = useMFAdminAppInitializer()

  const [isHovered, setIsHovered] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  const disclosure = useDisclosure()

  const [isDesktop] = useMediaQuery('(min-width: 62em)')

  const { pathname } = useLocation()

  const adminType = useMemo(() => {
    if (currentAdmin.is_super_admin) return 'Super Admin'
    if (currentAdmin.is_client_admin) return 'Client Admin'
    return 'Admin'
  }, [currentAdmin])

  const loggedInAs = useMemo(() => {
    if (currentAdmin.logged_in_as === 'Agency') return 'Agency'
    return 'Organization'
  }, [currentAdmin])

  /** @type {MFNavContextType['skin']} */
  const skinValue = useMemo(
    () => ({
      value: skin,
      /**
       * @param {import('react').MouseEvent<button>} e
       * @param {import('../../contexts/MFAdminAppInitializer/MFAdminAppInitializer.types').MFAdminLayoutSkinType} [value]
       */
      toggle: (e, value) => {
        e.preventDefault()
        SET_SKIN(value ?? (skin === 'dark' ? 'light' : 'dark'))
      }
    }),
    [SET_SKIN, skin]
  )

  /** @type {MFNavContextType['current']} */
  const currentValue = useMemo(
    () => ({
      admin: currentAdmin,
      agency: currentAgency,
      org: currentOrg,
      loggedInAs,
      type: adminType
    }),
    [adminType, currentAdmin, currentAgency, currentOrg, loggedInAs]
  )

  /** @type {MFNavContextType['menu']} */
  const menuValue = useMemo(() => {
    const sizeValue = isDesktop ? (localStorage.getItem('menuState') ?? menu.state) : 'drawer'
    return {
      size: {
        value: sizeValue,
        toggle: SET_MENU_STATE
      },
      state: {
        trueDockState: (isDesktop ? sizeValue : 'drawer') === 'dock' && !isHovered,
        isHovered,
        setIsHovered
      }
    }
  }, [SET_MENU_STATE, isDesktop, isHovered, menu.state])

  const navItemsValue = useMemo(() => {
    const permissions = currentAdmin?.admin_permissions

    if (permissions || permissions === false)
      return navWithPermissions(permissions, currentAdmin, skin, currentAgency).items

    return []
  }, [currentAdmin, currentAgency, skin])

  const breadCrumbsValue = useMemo(() => {
    const findBreadcrumbPath = (routes = navItemsValue, currentPath = pathname, breadcrumbTrail = []) => {
      for (const route of routes) {
        const { title, navLink, icon, id } = route
        const newBreadcrumbTrail = [...breadcrumbTrail, { title, navLink, icon, id }] // Push full route object

        // If the route matches, return the full breadcrumb path
        if (route.navLink === currentPath) return newBreadcrumbTrail

        // If there are children, search recursively
        if (route.children) {
          const found = findBreadcrumbPath(route.children, currentPath, newBreadcrumbTrail)
          if (found) return found
        }
      }
      return null // No match found
    }

    return findBreadcrumbPath()
  }, [navItemsValue, pathname])

  const value = useMemo(() => {
    return {
      ...disclosure,
      skin: skinValue,
      current: currentValue,
      menu: menuValue,
      modal: {
        value: modalOpen,
        toggle: setModalOpen
      },
      navItems: navItemsValue,
      breadCrumbs: breadCrumbsValue
    }
  }, [disclosure, skinValue, currentValue, menuValue, modalOpen, navItemsValue, breadCrumbsValue])

  const handleHotKey = useCallback(() => {
    const { isOpen, onOpen, onClose } = disclosure

    if (menuValue.size.value === 'drawer') {
      if (isOpen) onClose()
      else onOpen()
    } else if (menuValue.size.value === 'full') menuValue.size.toggle('dock')
    else if (menuValue.size.value === 'dock') menuValue.size.toggle('full')
    else onClose()
  }, [disclosure, menuValue.size])

  useKeyPress(['Meta', 'b'], handleHotKey)

  return (
    <MFNavContext.Provider value={value} {...props}>
      {children}
    </MFNavContext.Provider>
  )
}

MFNavProvider.propTypes = {
  children: PropTypes.node.isRequired
}
